/**
 * Fluxos guiados: as tarefas que atravessam várias telas.
 *
 * "Como começo do zero?" não é respondida por tela nenhuma — a resposta é uma
 * sequência. Declarados como dado, entregues um passo por vez.
 */

export interface FlowStep {
  text: string;
  /** Rota para abrir neste passo, quando houver. */
  route?: string;
  /** Documento da base que detalha este passo. */
  docId?: string;
}

export interface HelpFlow {
  id: string;
  /** Como o usuário perguntaria. Alimenta a busca. */
  question: string;
  /** Outras formas de perguntar a mesma coisa. */
  terms: string[];
  title: string;
  steps: FlowStep[];
  /** Planos necessários. Faltando, o chat DIZ que falta e a quem pedir. */
  requiresPlan?: string[];
}

export const HELP_FLOWS: HelpFlow[] = [
  {
    id: "comecar-do-zero",
    question: "como começo a usar o GIFI do zero?",
    terms: ["comecar", "primeiro passo", "novo aqui", "do zero", "configurar tudo", "iniciar"],
    title: "Colocar sua carteira no GIFI",
    steps: [
      {
        text:
          "Cadastre seus imóveis. Cada imóvel é a base de tudo: sem ele não há contrato, e sem " +
          "contrato não há cobrança.",
        route: "/imoveis",
        docId: "help:Meus Imóveis:Cadastrar um imóvel",
      },
      {
        text:
          "Crie o contrato de cada imóvel alugado, com valor, data de início e dia de vencimento. " +
          "Ao salvar, as cobranças mensais são criadas de uma vez.",
        route: "/contratos/novo",
        docId: "help:Novo Contrato:As cobranças que serão criadas",
      },
      {
        text:
          "Confira as cobranças no Painel Financeiro. Elas nascem pendentes e é aqui que você " +
          "acompanha os vencimentos.",
        route: "/cobrancas",
        docId: "help:Painel Financeiro:O que é esta tela",
      },
      {
        text:
          "Conforme os aluguéis entrarem, registre cada pagamento com a data e o valor recebido. " +
          "É isso que mantém os indicadores fiéis ao que entrou na conta.",
        route: "/cobrancas",
        docId: "help:Painel Financeiro:Registrar o pagamento",
      },
    ],
  },
  {
    id: "fechar-o-mes",
    question: "como eu fecho o mês?",
    terms: ["fechar o mes", "fechamento", "rotina mensal", "todo mes", "final do mes"],
    title: "Fechar o mês",
    steps: [
      {
        text:
          "No começo do mês, use \"Gerar Cobranças\" para criar os lançamentos de todos os " +
          "contratos ativos. Contratos que já têm cobrança no mês são ignorados.",
        route: "/cobrancas",
        docId: "help:Painel Financeiro:Gerar as cobranças do mês",
      },
      {
        text:
          "Ao longo do mês, registre cada recebimento. Cobranças vencidas já aparecem com multa e " +
          "juros somados no valor sugerido.",
        route: "/cobrancas",
        docId: "help:Painel Financeiro:Registrar o pagamento",
      },
      {
        text:
          "No fim do mês, filtre por \"Este mês\" e confira os quatro indicadores do topo. " +
          "Entradas e Saídas contam apenas o que já foi efetivamente pago.",
        route: "/cobrancas",
        docId: "help:Painel Financeiro:Os quatro indicadores do topo",
      },
      {
        text:
          "Se algum valor não bate, verifique se sobrou filtro de período ou de imóvel " +
          "selecionado, e se todos os recebimentos foram registrados.",
        route: "/cobrancas",
        docId: "help:Painel Financeiro:Filtrar por período e por imóvel",
      },
    ],
  },
  {
    id: "corrigir-lancamento-errado",
    question: "lancei uma cobrança errada, como corrijo?",
    terms: ["errado", "corrigir", "errei", "cobranca errada", "valor errado", "desfazer"],
    title: "Corrigir uma cobrança lançada errada",
    steps: [
      {
        text:
          "Se a cobrança ainda não foi paga, cancele-a no Painel Financeiro. Ela sai dos cálculos " +
          "e continua visível no histórico. O cancelamento é irreversível.",
        route: "/cobrancas",
        docId: "help:Painel Financeiro:Cancelar uma cobrança",
      },
      {
        text:
          "Se ela já está paga, o sistema recusa o cancelamento. Nesse caso o caminho é registrar " +
          "o valor correto no próximo ciclo e anotar o ajuste.",
        docId: "help:Painel Financeiro:Cancelar uma cobrança",
      },
      {
        text:
          "Se o erro está no valor do aluguel e vai se repetir todo mês, corrija o contrato: é " +
          "dele que saem o valor e as datas de todas as cobranças.",
        route: "/contratos",
        docId: "help:Novo Contrato:O que é esta tela",
      },
    ],
  },
];

export function findFlow(id: string): HelpFlow | undefined {
  return HELP_FLOWS.find((f) => f.id === id);
}
